import React, { useState, useEffect } from 'react';
import { Layout, Breadcrumb, Button, Upload, message, Spin, Typography, Table, Popconfirm, Tabs } from 'antd';
import { FilePdfOutlined, InboxOutlined, DownloadOutlined, DeleteOutlined, UploadOutlined, HistoryOutlined } from '@ant-design/icons';
import HeaderComponent from '../Shared/components/HeaderComponent';
import axios from 'axios';

const { Content } = Layout;
const { Dragger } = Upload;
const { Text } = Typography;

const FoliosLayout = () => {
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [loadingHistorial, setLoadingHistorial] = useState(false);

    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const fetchHistorial = async () => {
        setLoadingHistorial(true);
        try {
            const res = await axios.get('/api/v1/folios', config);
            setHistorial(res.data);
        } catch (e) {
            console.error('Error cargando historial:', e);
        } finally {
            setLoadingHistorial(false);
        }
    };

    useEffect(() => { fetchHistorial(); }, []); // eslint-disable-line

    const handleUpload = async () => {
        if (!file) {
            message.warning('Selecciona un archivo Excel primero');
            return;
        }
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('archivo', file);

            const response = await axios.post('/api/v1/folios/generar', formData, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'folios.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            message.success('PDF generado y descargado');
            setFile(null);
            fetchHistorial();
        } catch (error) {
            console.error('Error al generar folios:', error);
            // Intentar leer el mensaje de error del servidor (viene como blob por responseType: 'blob')
            let errorMsg = 'Error al generar el PDF.';
            if (error.response && error.response.data) {
                try {
                    const text = error.response.data instanceof Blob
                        ? await error.response.data.text()
                        : JSON.stringify(error.response.data);
                    const parsed = JSON.parse(text);
                    if (parsed.error) errorMsg = parsed.error;
                } catch (e) { /* ignore parse error */ }
            }
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleDescargar = async (record) => {
        try {
            const response = await axios.get(`/api/v1/folios/${record.id}/descargar`, {
                ...config,
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', record.nombre_archivo);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            message.error('Error al descargar');
        }
    };

    const handleEliminar = async (id) => {
        try {
            await axios.delete(`/api/v1/folios/${id}`, config);
            message.success('Eliminado');
            fetchHistorial();
        } catch (e) {
            message.error('Error al eliminar');
        }
    };

    const columns = [
        {
            title: 'Fecha',
            dataIndex: 'fecha_generacion',
            key: 'fecha',
            render: (val) => new Date(val).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
            sorter: (a, b) => new Date(a.fecha_generacion) - new Date(b.fecha_generacion),
            defaultSortOrder: 'descend'
        },
        { title: 'Folio Inicio', dataIndex: 'folio_inicio', key: 'folio_inicio' },
        { title: 'Folio Fin', dataIndex: 'folio_fin', key: 'folio_fin' },
        { title: 'Total', dataIndex: 'total_folios', key: 'total_folios', align: 'center' },
        {
            title: 'Acciones',
            key: 'acciones',
            align: 'center',
            render: (_, record) => (
                <span>
                    <Button type="link" icon={<DownloadOutlined />} onClick={() => handleDescargar(record)}>Descargar</Button>
                    <Popconfirm title="¿Eliminar este registro?" onConfirm={() => handleEliminar(record.id)} okText="Sí" cancelText="No">
                        <Button type="link" danger icon={<DeleteOutlined />}>Eliminar</Button>
                    </Popconfirm>
                </span>
            )
        }
    ];

    const uploadProps = {
        beforeUpload: (f) => {
            const isExcel = f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || f.name.endsWith('.xlsx');
            if (!isExcel) { message.error('Solo archivos .xlsx'); return Upload.LIST_IGNORE; }
            setFile(f);
            return false;
        },
        onRemove: () => setFile(null),
        maxCount: 1,
        accept: '.xlsx',
        fileList: file ? [file] : [],
    };

    const tabItems = [
        {
            key: 'generar',
            label: <span><UploadOutlined /> Generar Folios</span>,
            children: (
                <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Text style={{ display: 'block', marginBottom: 16 }}>
                        Sube un archivo Excel (.xlsx) con las columnas:<br />
                        <strong>Número de obra, Nombre de obra, Fecha, Proveedor, Cantidad, Concepto, Número de folio</strong>
                    </Text>

                    <Dragger {...uploadProps} style={{ marginBottom: 24 }}>
                        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                        <p className="ant-upload-text">Haz clic o arrastra un archivo Excel aquí</p>
                        <p className="ant-upload-hint">Solo archivos .xlsx</p>
                    </Dragger>

                    <Button type="primary" icon={loading ? <Spin size="small" /> : <FilePdfOutlined />}
                        onClick={handleUpload} disabled={!file || loading} block size="large">
                        {loading ? 'Generando PDF...' : 'Generar PDF de Folios'}
                    </Button>
                </div>
            )
        },
        {
            key: 'historial',
            label: <span><HistoryOutlined /> Historial</span>,
            children: (
                <Table
                    columns={columns}
                    dataSource={historial}
                    rowKey="id"
                    loading={loadingHistorial}
                    pagination={{ pageSize: 10 }}
                    size="small"
                    locale={{ emptyText: 'No hay folios generados aún' }}
                />
            )
        }
    ];

    return (
        <Layout>
            <Content>
                <Breadcrumb className='breadcrumb' items={[{ title: <a href="/home">Inicio</a> }, { title: 'Folios' }]} />
                <HeaderComponent title="Generador de Folios" />
                <Tabs defaultActiveKey="generar" items={tabItems} onChange={(key) => { if (key === 'historial') fetchHistorial(); }} />
            </Content>
        </Layout>
    );
};

export default FoliosLayout;

